<script setup>
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import ArticleApi from '@/api/ArticleApi';
import BoardApi from '@/api/BoardApi';
import { BoardTitleBox, Pagination } from '@/components/board';
import { addContentLogAPI } from '@/api/logServiceApi';
import IconView from '@/components/svg/IconView.vue';
import { patchEssayLike, patchArticleLike } from '@/api/EowayApi';
import { IconLike } from '@/components/svg';
import { useRecentSearches } from '@/composables/useRecentSearches';
import SearchBoxWithList from './SearchBoxWithList.vue';
import BoardCommentDrawer from './BoardCommentDrawer.vue';
import { DEV_MODE } from '@/config/dev-mode';
import { useDeviceDetect } from '@/composables/useDeviceDetect';

const props = defineProps({
  board: {
    type: String,
    default: '',
  },
  boardId: {
    type: Number,
    default: 0,
  },
});

const route = useRoute();
const router = useRouter();
const store = useStore();

const { isMobile } = useDeviceDetect();

const memberId = ref(null);
const memberInfo = ref(null);
const requestSize = computed(() => {
  if (props.board === 'faq') {
    return 100;
  } else if (props.board === 'word' || props.board === 'event') {
    return 9;
  } else {
    return 10;
  }
});
const paramData = ref({
  birthStart: null,
  startDate: null,
  endDate: null,
  genderOption: 0,
  page: 1,
  searchKeywords: '',
  sortOption: null,
  userGroupId: null,
  fiveEles: null,
  size: requestSize.value,
  sort: null,
  userId: null,
  categoryId: null,
  systemId: import.meta.env.VITE_APP_SYSTEMID,
});
const totalPages = ref(0);
const totalElements = ref(0);
const searchKeywords = ref('');
/** 게시물 목록 */
const listData = reactive([]);
/** 카테고리 목록 */
const categoryList = ref(null);
/** FAQ 컨텐츠 인덱스 */
const faqCurrentIndex = ref(null);
/** 검색결과 없음 팝업 상태 */
const isPopSearchNoneBox = ref(false);
/** 댓글 드로어 */
const isBoardCommentOpen = ref(false);
const activeArticleId = ref(null);

const tabCate = ref(null);
const eventCategory = ref('ALL');

onMounted(async () => {
  store.dispatch('saveCurrentBoard', props.board);

  const currentBoardPage = store.state.boards.currentBoardPage;
  const currentBoardKeyword = store.state.boards.currentBoardKeywords;
  if (currentBoardPage) {
    paramData.value.page = currentBoardPage;
  }
  if (currentBoardKeyword) {
    paramData.value.searchKeywords = currentBoardKeyword;
    searchKeywords.value = currentBoardKeyword;
  }
  await getCategory(props.boardId);

  tabCate.value = route.query.tabCate;
  if (tabCate.value) {
    await clickCategory(tabCate.value);
    paramData.value.categoryId = parseInt(tabCate.value);
  } else {
    await getList();
  }
});

watch(
  () => props.boardId,
  async (newVal, oldVal) => {
    // 게시판 이동했을 때
    if (newVal !== oldVal) {
      clearData();
      // store.dispatch('saveCurrentBoardKeywords', '')
      await getCategory(newVal);
      await getList();
    }
  },
);

/** 최근 검색어 */
// 최근검색어 key
const recentKey = computed(() => `board-${props.board ?? 'default'}`);
const recent = useRecentSearches(recentKey, { max: 10, ttlHours: 480, storage: 'local' });

/** paramData 및 listData 초기화 */
const clearData = () => {
  paramData.value = {
    birthStart: null,
    startDate: null,
    endDate: null,
    genderOption: 0,
    page: 1,
    searchKeywords: '',
    sortOption: null,
    userGroupId: null,
    fiveEles: null,
    size: 10,
    sort: null,
    userId: null,
    categoryId: null,
  };
  listData.value = [];
  searchKeywords.value = '';
  categoryList.value = null;
};

/** 게시판 카테고리 요청 */
// const getCategory = async (boardId) => {
//   if (props.board !== 'faq' && props.board !== 'word') return

//   try {
//     categoryList.value = await BoardApi().categoryById(boardId)
//     if (categoryList.value) paramData.value.categoryId = categoryList.value[0]?.categoryId
//   } catch (e) {
//     alert(e)
//   }
// }
// 개발/운영 구분 ver
const getCategory = async (boardId) => {
  if (props.board !== 'faq' && props.board !== 'word') return;
  try {
    const result = await BoardApi().categoryById(boardId);
    categoryList.value = result;
    if (categoryList.value && categoryList.value.length > 0) {
      paramData.value.categoryId = categoryList.value[0]?.categoryId;
    }
  } catch (e) {
    alert(e);
  }
};

/** 게시판의 게시물 목록 요청 */
const getList = async () => {
  await nextTick();
  if (props.boardId) {
    // 1:1문의 또는 명리상담 작성글 호출 시에만
    if (props.board === 'inquiry' || props.board === 'counseling') {
      memberInfo.value = store.getters;
      memberId.value = memberInfo.value['user/id'];

      if (memberId.value) {
        paramData.value.userId = memberId.value;
      } else {
        if (confirm('로그인 후 이용 가능합니다. 로그인 페이지로 이동하시겠습니까?')) {
          router.push('/auth/login');
        } else {
          router.go(-1);
          return;
        }
      }
    } else {
      // paramData.value.size = -1;
    }
    if (searchKeywords.value) {
      recent.add(searchKeywords.value);
      // paramData.value.page = 1;
      store.dispatch('saveCurrentBoardKeywords', searchKeywords.value);
    }

    if (props.board === 'event' || props.board === 'word') {
      paramData.value.size = 9;
    }

    // 이벤트 탭에 따라 isActiveEvent 파라미터 설정
    if (props.board === 'event') {
      if (eventCategory.value === 'IN_PROGRESS') {
        paramData.value.isActiveEvent = true;
      } else if (eventCategory.value === 'DONE') {
        paramData.value.isActiveEvent = false;
      } else {
        delete paramData.value.isActiveEvent;
      }
    }

    paramData.value.searchKeywords = searchKeywords.value;

    try {
      const result = await ArticleApi().list(props.boardId, paramData.value);

      if (result) {
        if (searchKeywords.value && result.content.length === 0) {
          totalElements.value = result.totalElements;
          setPopSearchNoneBox(true);
          return;
        }
        listData.value = [...result.content];
        totalPages.value = result.totalPages;
        totalElements.value = result.totalElements;
        isPopSearchNoneBox.value = false;
      }
    } catch (error) {
      alert(error);
    }
  }
};

/** 검색어 업데이트 */
// const updateSearchKeywords = (newValue) => (searchKeywords.value = newValue)
const updateSearchKeywords = (newValue) => {
  const next = (newValue ?? '').trim();
  const curr = (searchKeywords.value ?? '').trim();
  if (next === curr) return;
  searchKeywords.value = newValue;
  paramData.value.page = 1;
  store.dispatch('saveCurrentBoardPage', paramData.value.page);
};

/** 이벤트 게시물 이미지 추출 */
const extractFirstImageSrc = (htmlContent) => {
  const div = document.createElement('div');
  div.innerHTML = htmlContent;
  const img = div.querySelector('img');
  return img ? img.src : null;
};

const boardMap = {
  notice: 'N_notice_article',
  event: 'N_event_article',
  faq: 'N_faq_article',
  word: 'N_guide_article',
};
/** 게시물로 이동 */
const goView = (id, scrollToComment = false) => {
  const tabCate = paramData.value.categoryId;
  let url = `/nangman-center/${props.board}/${id}`;

  if (tabCate) {
    url += `?cate=${tabCate}`;
    if (scrollToComment) {
      url += `&scrollToComment=true`;
    }
  } else if (scrollToComment) {
    url += `?scrollToComment=true`;
  }

  if (boardMap[props.board]) {
    addContentLogAPI({
      contentType: boardMap[props.board],
      servicePk: id,
    });
  }

  router.push(url);
};

/** 1:1문의 또는 명리상담 작성하기 버튼  */
const writeInquiry = () => router.push(`/nangman-center/${props.board}/write`);

const categoryAll = ref(true);
/** 자주묻는질문 카테고리 선택 */
const clickCategory = (cate) => {
  faqCurrentIndex.value = null;
  if (route.query.tabCate !== cate) {
    paramData.value.searchKeywords = '';
    searchKeywords.value = '';
  }

  paramData.value.categoryId = cate;

  addContentLogAPI({
    contentType: props.board === 'faq' ? 'N_faq_category' : 'N_guide_category',
    servicePk: cate,
  });

  // if (cate === 'all') {
  //   paramData.value.categoryId = null
  //   categoryAll.value = true
  // } else {
  //   categoryAll.value = false
  // }
  const q = { ...route.query };
  if (cate === 'all' || cate == null) {
    delete q.tabCate;
    paramData.value.categoryId = null;
  } else {
    q.tabCate = String(cate);
    paramData.value.categoryId = cate;
  }
  router.replace({ query: q });
  getList();
};

/** 자주묻는질문 컨텐츠 열기 */
const toggleContent = ({ item, index }) => {
  if (faqCurrentIndex.value === index) faqCurrentIndex.value = null;
  else {
    addContentLogAPI({
      contentType: boardMap['faq'],
      servicePk: item.articleId,
    });
    faqCurrentIndex.value = index;
  }
};

/** 검색결과 없음 팝업 상태관리 */
const setPopSearchNoneBox = (state) => (isPopSearchNoneBox.value = state);

/** 페이지 변경 */
const changePage = (page) => {
  paramData.value.page = page;
  getList();
  store.dispatch('saveCurrentBoardPage', page);
};

const divStyle = (item) => {
  if (item.attachFiles && item.attachFiles.length) return null;
  return {
    width: '100%',
    height: '100%',
    borderRadius: '8px',
    // background: 'rgba(0, 0, 0, 0.05)',
  };
};

/** 좋아요 버튼 클릭 - 이벤트 버블링 중단 */
const handleBubbling = (event, essayId) => {
  event.stopPropagation();
  patchLike(essayId);
};

/** 좋아요 업데이트 */
const patchLike = async (essayId) => {
  const result = await patchEssayLike(essayId);
  const clickedEssay = listData.value.find((item) => item.essayId === essayId);
  clickedEssay.likesChecked = result.likeClicked ? 1 : 0;
};

/** 좋아요 버튼 클릭 - 이벤트 버블링 중단 */
const handleBubblingArticle = (event, articleId) => {
  event.stopPropagation();
  patchLikeArticle(articleId);
};

/** 좋아요 업데이트 */
const patchLikeArticle = async (articleId) => {
  const result = await patchArticleLike(articleId);
  const clickedEssay = listData.value.find((item) => item.articleId === articleId);
  clickedEssay.likeChecked = result.likeClicked ? true : false;
};

/** 이벤트 카테고리 선택 */
const clickEventCategory = async (category) => {
  eventCategory.value = category;
  paramData.value.page = 1;
  await getList();
};

/** 검색창 X 클릭시 초기화 */
// 이미 초기 상태일 경우 다시 요청하지 않도록 조건 검사
const DEFAULTS = computed(() => ({
  searchKeywords: '',
  categoryId: categoryList.value?.[0]?.categoryId ?? null,
}));
function onSearchClear() {
  const p = paramData.value;
  const def = DEFAULTS.value;
  const isInitial =
    (p.searchKeywords ?? '') === DEFAULTS.searchKeywords &&
    (p.categoryId ?? null) === DEFAULTS.categoryId;

  if (isInitial) return;
  Object.assign(p, { searchKeywords: def.searchKeywords, categoryId: def.categoryId, page: 1 });
  store.dispatch('saveCurrentBoardKeywords', '');
  getList();
}

/** body 스크롤 제어 */
function toggleBodyScroll(disable) {
  if (disable) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
}

/** 댓글창 열기 */
function openReplyDrawer(itemOrId) {
  if (!isMobile) return;
  const id = typeof itemOrId === 'object' ? itemOrId.articleId : itemOrId;
  if (id == null) return;

  activeArticleId.value = id;
  isBoardCommentOpen.value = true;
  toggleBodyScroll(true); // body 스크롤 숨기기
  // console.log('드로어', activeArticleId.value, isBoardCommentOpen.value)
}

function closeReplyDrawer() {
  isBoardCommentOpen.value = false;
  activeArticleId.value = null;
  toggleBodyScroll(false); // body 스크롤 복원
}

// ========================= 모바일 앱과 통신 =========================
/** 모바일 하단 여백 */
const insetBottom = ref(20);

onMounted(async () => {
  window.addEventListener('message', handleMessage);
  document.addEventListener('message', handleMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', handleMessage);
  document.removeEventListener('message', handleMessage);
});

/** 모바일 앱과 통신 */
const handleMessage = async (event) => {
  try {
    let data;

    if (typeof event.data === 'string') {
      data = JSON.parse(event.data);
    } else {
      data = event.data;
    }

    if (data.source === 'react-devtools-content-script') return;
    if (data.key === '__VUE_DEVTOOLS_VUE_DETECTED_EVENT__') return;

    // 인증 상태는 글로벌 RN 메시지 핸들러에서만 관리한다.
    if (data.action === 'init') {
      if (data?.payload?.bottom) insetBottom.value = data?.payload?.bottom;
    }
  } catch (e) {
    console.error('Invalid message received:', `${e}`);
  }
};
</script>

<template>
  <div class="st-board-list board" :style="isMobile ? { paddingBottom: `${insetBottom}px` } : {}">
    <div
      :class="{
        'mobile-board__sticky-top': isMobile,
        'wrap-as-none': !isMobile,
      }"
    >
      <BoardTitleBox :board="board">
        <template v-slot:add-on>
          <!-- <SearchBox
            v-if="board !== 'event'"
            :placeholder="placeholder"
            @update:searchKeywords="updateSearchKeywords"
            @search="getList"
            @clear="onSearchClear"
          /> -->
          <!-- TEST - 최근 검색어 리스트 표시 -->
          <SearchBoxWithList
            v-if="board !== 'event'"
            :key="recentKey"
            :placeholder="placeholder"
            :search-keywords="searchKeywords"
            :recent-key="recentKey"
            :recent="recent"
            @update:searchKeywords="updateSearchKeywords"
            @search="getList"
            @clear="onSearchClear"
          />
        </template>
      </BoardTitleBox>

      <div v-if="board === 'inquiry' || board === 'counseling'" class="board-inquiry-box">
        <div class="tit-box">
          <div class="txt1">운영 시간 안내</div>
          <div class="txt2">답변시간 평일 13:00 - 18:00 (주말, 공휴일 휴무)</div>
          <div class="txt3">답변 시간 이후 접수 건은 운영시간 내 순차적으로 답변해 드립니다.</div>
        </div>
        <button class="btn-do" @click="writeInquiry()">
          {{ board === 'inquiry' ? '1:1 문의하기' : '이벤트 신청' }}
        </button>
      </div>

      <div v-if="board === 'faq' || board === 'word'" class="category-list-box">
        <!-- <button
          v-if="board === 'word'"
          class="btn-category"
          :class="{ active: categoryAll }"
          @click="clickCategory('all')"
        >
          전체
        </button> -->

        <button
          v-if="categoryList"
          v-for="category in categoryList"
          :key="category.categoryId"
          :class="{ active: paramData.categoryId === category.categoryId }"
          class="btn-category"
          @click="clickCategory(category.categoryId)"
        >
          {{ category.categoryCtn }}
        </button>
      </div>

      <div v-if="board === 'event'" class="category-list-box">
        <button
          class="btn-category"
          :class="{ active: eventCategory === 'ALL' }"
          @click="clickEventCategory('ALL')"
        >
          전체
        </button>
        <button
          class="btn-category"
          :class="{ active: eventCategory === 'IN_PROGRESS' }"
          @click="clickEventCategory('IN_PROGRESS')"
        >
          진행중 이벤트
        </button>
        <button
          class="btn-category"
          :class="{ active: eventCategory === 'DONE' }"
          @click="clickEventCategory('DONE')"
        >
          종료된 이벤트
        </button>
      </div>
      <div
        class="board__search-results"
        :class="{ 'board__search-results--notice': board === 'notice' }"
      >
        <div class="search-result__total">
          결과 <span>{{ totalElements }}</span
          >건
        </div>
        <div class="search-result__"></div>
      </div>
      <div v-if="isPopSearchNoneBox" class="search-result__underline"></div>
    </div>

    <!-- <div v-if="board === 'word'" class="category-list-box">
      <button class="btn-category active">전체</button>
      <button class="btn-category">천간/지지</button>
      <button class="btn-category">합충형파해</button>
    </div> -->

    <div v-if="isPopSearchNoneBox" class="board-list-box board-list__none">
      검색 결과가 없습니다.
    </div>
    <div
      v-if="
        board !== 'faq' &&
        board !== 'inquiry' &&
        board !== 'notice' &&
        board !== 'event' &&
        board !== 'word' &&
        board !== 'counseling'
      "
      class="board-list-box"
    >
      <table>
        <colgroup>
          <col width="*" />
          <col width="150px" />
        </colgroup>
        <thead>
          <tr>
            <th>제목</th>
            <th>등록일</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in listData.value" :key="item.id">
            <td class="al-tit" @click="goView(item.articleId)">
              <span class="tit">{{ item.articleTtl }}</span>
            </td>
            <td class="al-center">
              <span class="date">{{ formatCustomDate(item.createdAt) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="(board === 'event' || board === 'word') && isPopSearchNoneBox == false"
      class="grid-container event-st-list"
    >
      <div
        v-for="(item, index) in listData.value"
        :key="index"
        class="item"
        @click="goView(item.articleId)"
      >
        <div class="img-box-lg">
          <!-- <div class="">{{ item.eventStatus }}</div> -->
          <div class="img-box" v-if="item.thumbnail">
            <img :src="item.thumbnail?.publishedUri" alt="" />
          </div>
          <div v-else class="none-thumbnail"></div>
          <!-- <img
            v-if="item.articleCtn && extractFirstImageSrc(item.articleCtn)"
            :src="extractFirstImageSrc(item.articleCtn)"
            style="max-height: none"
          />

          <div v-else :style="divStyle(item)"></div> -->
          <!-- <div class="like-box">
            <button
              class="txt-like"
              @click="(event) => handleBubblingArticle(event, item.articleId)"
            >
              <IconLike :size="20" class="icon-like" :class="{ active: item.likeChecked }" />
            </button>
          </div> -->
        </div>
        <div class="info-box">
          <div class="event-gird__reaction-box">
            <div class="reaction__like-reply">
              <div class="reaction-box">
                <button
                  class="like-btn"
                  @click="(event) => handleBubblingArticle(event, item.articleId)"
                >
                  <!-- <IconLike :size="20" class="icon-like" :class="{ active: item.likeChecked }" /> -->
                  <IconLike :size="20" class="icon-like" :class="{ active: item.likeChecked }" />
                </button>
                <span class="reaction-count">{{ item.likeCount }}</span>
              </div>
              <!-- TODO: 댓글기능 DEV_MODE 제한 제거 -->
              <div v-if="DEV_MODE && item.hasComment === true" class="reaction-box">
                <!-- <button class="reply-btn" @click.stop="openReplyDrawer(item)"></button> -->
                <button class="reply-btn" @click.stop="goView(item.articleId, true)"></button>
                <span class="reaction-count">{{ item.commentCount }}</span>
              </div>
            </div>
            <div class="reaction-box">
              <div class="view-icon"><IconView /></div>
              <span class="view-count">{{ item.viewCount }}</span>
            </div>
          </div>
          <div class="txt-date" v-if="board === 'event'">
            <span class="date-box"
              >{{ formatCustomDate(item.eventStartDt) }} ~
              {{ formatCustomDate(item.eventEndDt) }}</span
            >
            <!-- <span class="hit-box">
              <IconView /><span>{{ item.viewCount }}</span>
            </span> -->
          </div>
          <div class="txt-tit-box">
            <div class="txt-tit">
              {{ item.articleTtl }}
            </div>
            <div class="txt-tit-sub">{{ item.articleTtlSub }}</div>
          </div>
          <div class="art-icon-box" v-if="board === 'word' && item.icon">
            <span
              v-for="(icon, index2) in item.icon ? item.icon.split(',') : []"
              :key="index2"
              class="txt"
              :class="icon"
            >
              {{ icon === 'RECO' ? '추천' : icon }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="board === 'inquiry' || board === 'counseling'" class="board-list-box">
      <table>
        <colgroup>
          <col width="120px" />
          <col width="*" />
          <col width="120px" />
          <col width="120px" />
          <col width="120px" />
        </colgroup>
        <thead>
          <tr>
            <th>카테고리</th>
            <th>제목</th>
            <th>작성자</th>
            <th>등록일</th>
            <th>답변상태</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in listData.value" :key="item.id">
            <td class="al-center">
              {{ item.boardCategory?.categoryCtn }}
            </td>
            <td class="al-tit" @click="goView(item.articleId)">
              {{ item.articleTtl }}
            </td>
            <td class="al-center">
              <span class="st-name">{{ item.userNickName }}</span>
            </td>
            <td class="al-center st-date">
              <span class="st-date">{{ formatCustomDate(item.createdAt) }}</span>
            </td>
            <td class="al-center st-state" :class="{ finish: item.comments?.length > 0 }">
              <span class="st-state">{{
                item.comments?.length > 0 ? '답변완료' : '답변대기'
              }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- <div v-if="board === 'faq'" class="faq-list-wrap">
      <div v-for="(item, index) in listData.value" :key="item.id" class="faq-list-row">
        <div
          class="tit"
          :class="{ open: faqCurrentIndex === index }"
          @click="toggleContent({ item, index })"
        >
          {{ item.articleTtl }}
        </div>
        <div class="cont" :class="{ open: faqCurrentIndex === index }">
          <div v-html="item.articleCtn" />
        </div>
      </div>
    </div> -->
    <div v-if="board === 'faq' && isPopSearchNoneBox == false" class="faq-list-st2-wrap">
      <div
        v-for="(item, index) in listData.value"
        :key="item.id"
        @click="goView(item.articleId)"
        class="faq-list-st2-row"
      >
        <div class="tit-box" @click="goView(item.articleId)">
          <div class="tit">
            {{ item.articleTtl }}
          </div>
        </div>
        <div class="date-box">
          <button class="txt-like" @click="(event) => handleBubblingArticle(event, item.articleId)">
            <IconLike :size="20" class="icon-like" :class="{ active: item.likeChecked }" />
          </button>
        </div>
      </div>
    </div>

    <!-- 공지사항 -->
    <div v-if="board === 'notice' && isPopSearchNoneBox == false" class="notice-list-wrap">
      <div
        v-for="(item, index) in listData.value"
        :key="item.id"
        class="notice-list-row"
        :class="item.isNotice ? 'st-top' : ''"
        @click="goView(item.articleId)"
      >
        <div class="tit-box">
          <div class="tit-b">
            <div class="tit">{{ item.articleTtl }}</div>
            <div class="art-icon-box">
              <span
                v-for="(icon, index2) in item.icon ? item.icon.split(',') : []"
                :key="index2"
                class="txt"
                :class="icon"
              >
                {{ icon === 'RECO' ? '추천' : icon }}
              </span>
            </div>
          </div>
          <div class="tit-s">{{ item.articleTtlSub }}</div>
        </div>
        <div class="date-box">
          <div class="date">{{ formatCustomDate(item.displayDate) }}</div>
          <div class="hit">
            <span class="icon-hit"><IconView /></span><span>{{ item.viewCount }}</span>
          </div>
        </div>
      </div>

      <div
        v-if="isMobile"
        v-for="(item, index) in listData.value"
        :key="item.id"
        class="notice-mobile-list-row"
        :class="item.isNotice ? 'st-top' : ''"
        @click="goView(item.articleId)"
      >
        <div class="tit-box">
          <div class="date-box">
            <div class="date">{{ formatCustomDate(item.displayDate) }}</div>
            <div class="hit">
              <span class="icon-hit"><IconView /></span><span>{{ item.viewCount }}</span>
            </div>
          </div>
          <div class="tit-b">
            <div class="tit">{{ item.articleTtl }}</div>
          </div>
          <div class="tit-s">{{ item.articleTtlSub }}</div>
        </div>
        <div v-if="item.icon" class="art-icon-box">
          <span
            v-for="(icon, index2) in item.icon ? item.icon.split(',') : []"
            :key="index2"
            class="txt"
            :class="icon"
          >
            {{ icon === 'RECO' ? '추천' : icon }}
          </span>
        </div>
      </div>
    </div>

    <!-- <Pagination
      v-if="totalElements > 0 && listData.value.length > 0 && board !== 'faq'"
      :total-pages="totalPages"
      :current-page="paramData.page"
      :max-visible-pages="10"
      @page-changed="changePage"
    /> -->
    <Pagination
      v-if="totalElements > 0 && listData.value && listData.value.length > 0 && board !== 'faq'"
      :total-pages="totalPages"
      :current-page="paramData.page"
      :max-visible-pages="isMobile ? 5 : 10"
      @page-changed="changePage"
    />

    <!-- <PopSearchNoneBox v-if="isPopSearchNoneBox" @close="setPopSearchNoneBox(false)" /> -->
  </div>

  <!-- TODO: 댓글기능 DEV_MODE 제한 제거 -->
  <BoardCommentDrawer
    v-if="DEV_MODE"
    v-model="isBoardCommentOpen"
    :article-id="activeArticleId"
    @close="closeReplyDrawer"
  />
</template>
